import { ApiError } from "../errors.js";
import { parseContentDispositionFilename } from "../siteExport.js";
import { clearMenuCache, MENU_CACHE_TTL_MS, readMenuCache, writeMenuCache } from "../menuCache.js";
import { AdminClient } from "./admin.js";
export class SiteClient extends AdminClient {
    /**
     * Requests a production publish (POST /v1/sites/{slug}/publish). Needs a verified email.
     * Resolves `published` on a paid plan or an operator-approved slug, otherwise `pending_approval`.
     */
    async requestPublish(slug, checklist) {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/publish`, checklist, true);
    }
    /** Latest publish request of the site (GET /v1/sites/{slug}/publish/status); 404 until one exists. */
    async getPublishStatus(slug) {
        return this.request("GET", `/v1/sites/${encodeURIComponent(slug)}/publish/status`, undefined, true);
    }
    /** Lists the signed-in tenant's active developer API keys (the plaintext is never returned again). */
    async listDeveloperKeys() {
        const res = await this.request("GET", "/v1/developer/keys", undefined, true);
        return res.keys;
    }
    /** Mints a scoped machine key; the plaintext `key` is returned once. Needs a verified email. */
    async createDeveloperKey(input = {}) {
        return this.request("POST", "/v1/developer/keys", input, true);
    }
    /** Revokes one developer key (DELETE /v1/developer/keys/{id}). */
    async deleteDeveloperKey(id) {
        await this.request("DELETE", `/v1/developer/keys/${encodeURIComponent(id)}`, undefined, true);
    }
    /**
     * Fetches a site's public menu (no auth). Uses a short sessionStorage TTL (#614 B).
     * `location` selects the kitchen menu and its own cache entry. `fresh` skips the cache.
     * @deprecated Use `client.catalog.menu` (#1044); kept as an alias on /v1.
     */
    async getMenu(slug, opts) {
        const location = opts?.location?.trim().toLowerCase() ?? "";
        if (!opts?.fresh) {
            const cached = readMenuCache(this.menuCacheStore, slug, Date.now(), MENU_CACHE_TTL_MS, location);
            if (cached)
                return cached;
        }
        const query = location ? `?location=${encodeURIComponent(location)}` : "";
        const menu = await this.request("GET", `/v1/sites/${encodeURIComponent(slug)}/menu${query}`);
        writeMenuCache(this.menuCacheStore, slug, menu, Date.now(), location);
        return menu;
    }
    /** Owner list plus the menu catalog used to build a sale. */
    async listSiteSales(slug) {
        return this.request("GET", `/v1/sites/${encodeURIComponent(slug)}/sales`, undefined, true);
    }
    async createSiteSale(slug, input) {
        const sale = await this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/sales`, input, true);
        clearMenuCache(this.menuCacheStore, slug);
        return sale;
    }
    async updateSiteSale(slug, id, input) {
        const sale = await this.request("PUT", `/v1/sites/${encodeURIComponent(slug)}/sales/${encodeURIComponent(id)}`, input, true);
        clearMenuCache(this.menuCacheStore, slug);
        return sale;
    }
    async deleteSiteSale(slug, id) {
        await this.request("DELETE", `/v1/sites/${encodeURIComponent(slug)}/sales/${encodeURIComponent(id)}`, undefined, true);
        clearMenuCache(this.menuCacheStore, slug);
    }
    /**
     * Server price for the current cart. Does not start payment or place an order.
     * successUrl and cancelUrl are ignored.
     */
    async quoteCheckout(slug, input) {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/checkout/quote`, input);
    }
    /** Fetches public site metadata (no auth). */
    async getSite(slug) {
        return this.request("GET", `/v1/sites/${encodeURIComponent(slug)}`);
    }
    /**
     * Fetches a site's owner-editable content: hours, hero, contact. Public readers get the published
     * view. When an access token is stored it is sent so content:write staff of this site also receive
     * draft/archived newsletters (lumora#1166); a rejected token falls back to the anonymous read.
     */
    async getSiteContent(slug) {
        const path = `/v1/sites/${encodeURIComponent(slug)}/content`;
        if (!this.getAccessToken())
            return this.request("GET", path);
        try {
            return await this.request("GET", path, undefined, true);
        }
        catch (e) {
            if (e instanceof ApiError && e.status === 401)
                return this.request("GET", path);
            throw e;
        }
    }
    /**
     * Proxies Google Place Details reviews for a site (no auth). The API key stays
     * on the server. When `configured` is false, shells keep placeholder quotes (car-doctor#58).
     */
    async getGooglePlaceReviews(slug, placeId) {
        const q = new URLSearchParams({ placeId: placeId.trim() });
        return this.request("GET", `/v1/sites/${encodeURIComponent(slug)}/reviews/google?${q}`);
    }
    /**
     * Live Google reviews, ranked server-side (rating, recency, text length, helpfulness) and
     * cached. `location` is a pickup-location slug (falls back to the tenant place id). No auth.
     * `configured: false` means keep the site's own quotes (lumora#1322).
     */
    async getSiteReviews(slug, opts = {}) {
        const q = new URLSearchParams();
        if (opts.location?.trim())
            q.set("location", opts.location.trim());
        if (opts.limit && opts.limit > 0)
            q.set("limit", String(Math.floor(opts.limit)));
        const qs = q.toString();
        return this.request("GET", `/v1/sites/${encodeURIComponent(slug)}/reviews${qs ? `?${qs}` : ""}`);
    }
    /**
     * Updates a site's content (authenticated). The caller may only edit their own
     * tenant; a platform admin may edit any. Returns the stored content.
     */
    async updateSiteContent(slug, input) {
        return this.request("PUT", `/v1/sites/${encodeURIComponent(slug)}/content`, input, true);
    }
    /** The email blocks of the site content (public read). Each is absent on an API without email support. */
    async getEmailSettings(slug) {
        const c = await this.getSiteContent(slug);
        return {
            ...(c.emailConfirmations ? { emailConfirmations: c.emailConfirmations } : {}),
            ...(c.emailReminders ? { emailReminders: c.emailReminders } : {}),
            ...(c.emailFrom ? { emailFrom: c.emailFrom } : {}),
            smsRemindersLocked: c.smsRemindersLocked === true,
        };
    }
    /**
     * Saves only the email blocks given (PUT /content, content:write). Other content is left as stored.
     * 400 on an unknown template variable; see `classifySiteContentError`.
     */
    async updateEmailSettings(slug, input) {
        const c = await this.updateSiteContent(slug, input);
        return {
            ...(c.emailConfirmations ? { emailConfirmations: c.emailConfirmations } : {}),
            ...(c.emailReminders ? { emailReminders: c.emailReminders } : {}),
            ...(c.emailFrom ? { emailFrom: c.emailFrom } : {}),
            smsRemindersLocked: c.smsRemindersLocked === true,
        };
    }
    /**
     * Replaces only the Meet the Team roster (authenticated, content:write on the
     * caller's own site). Every other content field is left as stored.
     */
    async updateSiteTeam(slug, team) {
        return this.request("PUT", `/v1/sites/${encodeURIComponent(slug)}/content/team`, { team }, true);
    }
    /** Shop labor rate in cents (GET /settings/labor, any signed-in staff); 16000 until a supervisor sets one. */
    async getLaborRate(slug) {
        return this.request("GET", `/v1/sites/${encodeURIComponent(slug)}/settings/labor`, undefined, true);
    }
    /** Sets the shop labor rate (PUT /settings/labor, needs employees:manage). New lines only. */
    async setLaborRate(slug, laborRateCents) {
        return this.request("PUT", `/v1/sites/${encodeURIComponent(slug)}/settings/labor`, { laborRateCents }, true);
    }
    /** One generic tenant setting (GET /settings/{key}, any signed-in staff). `labor` and `booking` are exposed; other keys are 404. */
    async getSetting(slug, key) {
        return this.request("GET", `/v1/sites/${encodeURIComponent(slug)}/settings/${encodeURIComponent(key)}`, undefined, true);
    }
    /** Replaces one setting (PUT /settings/{key}); the write capability depends on the key. Resolves with the stored JSON. */
    async putSetting(slug, key, value) {
        return this.request("PUT", `/v1/sites/${encodeURIComponent(slug)}/settings/${encodeURIComponent(key)}`, value, true);
    }
    /** Booking rules (`{}` until set). */
    async getBookingSettings(slug) {
        return (await this.getSetting(slug, "booking")) ?? {};
    }
    /** Replaces the booking rules (content:write). 400 on an unknown field, mode, limit, window or malformed HH:MM. */
    async setBookingSettings(slug, settings) {
        return this.putSetting(slug, "booking", settings);
    }
    /**
     * Force-closes or reopens ordering for a site (lumora#1177, authenticated,
     * content:write on the caller's own site). `closed: false` reopens
     * immediately and `scope` is ignored. `closed: true` requires `scope`:
     * "today" auto-reopens at local midnight; "indefinite" stays closed until a
     * later call with `closed: false`. Every other content field is left as
     * stored. Returns the stored content.
     */
    async setEmergencyClose(slug, input) {
        return this.request("PUT", `/v1/sites/${encodeURIComponent(slug)}/emergency-close`, input, true);
    }
    /** Fetches a site's public hosting-plan catalog (no auth). */
    async getPlans(slug) {
        const res = await this.request("GET", `/v1/sites/${encodeURIComponent(slug)}/plans`);
        return res.plans;
    }
    /**
     * Creates a server-priced Stripe Checkout session for a public cart (no auth).
     * Item prices are computed server-side from the site's menu. Returns the
     * hosted checkout URL to redirect the customer to. Put `{STATUS_TOKEN}` in
     * `successUrl` to receive the order status token on return. Pass
     * `idempotencyKey` so a retry of the same cart returns the original session.
     */
    async createCheckout(slug, input, opts = {}) {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/checkout`, input, false, false, opts.idempotencyKey ? { "Idempotency-Key": opts.idempotencyKey } : undefined);
    }
    /**
     * Starts a Connect Checkout session for a booking deposit.
     * Pass authorized=true when the caller holds a staff access token for the site.
     * Public callers leave authorized false and include customerContact.
     */
    async createBookingDeposit(slug, bookingId, input, authorized = false) {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/bookings/${encodeURIComponent(bookingId)}/deposit`, input, authorized);
    }
    async startContactVerification(slug, email, purpose = "pay_at_restaurant") {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/contact-verifications`, {
            email,
            purpose,
        });
    }
    async confirmContactVerification(slug, id, code) {
        await this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/contact-verifications/${encodeURIComponent(id)}/confirm`, {
            code,
        });
    }
    /** Tenant-owner order history (base package). Optional filters match GET /v1/orders (#1320). */
    async listMyOrders(params) {
        return (await this.listMyOrdersPage(params)).orders;
    }
    /**
     * One page of the owner order history, newest first (GET /v1/orders). Pass either positional
     * `(limit, before)` or a filter object (`status`, `location`, `from`, `to`, `limit`, `before`).
     * `before` is an exclusive RFC3339 created-at cursor; pass the previous page's `nextBefore`.
     * `nextBefore` is absent on the last page.
     */
    async listMyOrdersPage(limitOrParams, before) {
        const params = typeof limitOrParams === "object" && limitOrParams !== null
            ? limitOrParams
            : { ...(limitOrParams !== undefined ? { limit: limitOrParams } : {}), ...(before ? { before } : {}) };
        const qs = new URLSearchParams();
        if (params.limit !== undefined && params.limit > 0)
            qs.set("limit", String(Math.floor(params.limit)));
        for (const k of ["status", "location", "from", "to", "before"]) {
            const v = params[k];
            if (v)
                qs.set(k, v);
        }
        const q = qs.toString();
        const res = await this.request("GET", q ? `/v1/orders?${q}` : "/v1/orders", undefined, true);
        return {
            orders: Array.isArray(res.orders) ? res.orders : [],
            ...(typeof res.nextBefore === "string" && res.nextBefore ? { nextBefore: res.nextBefore } : {}),
        };
    }
    async getMyOrder(id) {
        return this.request("GET", `/v1/orders/${encodeURIComponent(id)}`, undefined, true);
    }
    /** Owner refund for a paid online order (Stripe). Omit amountCents to refund the rest. */
    async refundMyOrder(id, amountCents) {
        const body = amountCents && amountCents > 0 ? { amountCents } : {};
        return this.request("POST", `/v1/orders/${encodeURIComponent(id)}/refund`, body, true);
    }
    /**
     * Creates or edits a menu category (platform admin only). The id is stable and
     * caller-supplied, so re-issuing it edits in place (idempotent). This is the
     * "modify the menu without SQL" path; no editor UI ships yet (see issue #43).
     * @deprecated Use `client.catalog.upsertMenuCategory` (#1044); kept as an alias on /v1.
     */
    async upsertMenuCategory(slug, id, input) {
        const cat = await this.request("PUT", `/v1/sites/${encodeURIComponent(slug)}/menu/categories/${encodeURIComponent(id)}`, input, true);
        clearMenuCache(this.menuCacheStore, slug);
        return cat;
    }
    /**
     * Creates or edits a menu item (platform admin only). Idempotent on the id.
     * @deprecated Use `client.catalog.upsertMenuItem` (#1044); kept as an alias on /v1.
     */
    async upsertMenuItem(slug, id, input) {
        const item = await this.request("PUT", `/v1/sites/${encodeURIComponent(slug)}/menu/items/${encodeURIComponent(id)}`, input, true);
        clearMenuCache(this.menuCacheStore, slug);
        return item;
    }
    /**
     * Owner/admin PATCH of a menu item (catalog gate on the server).
     * @deprecated Use `client.catalog.patchMenuItem` (#1044); kept as an alias on /v1.
     */
    async patchMenuItem(slug, id, input) {
        const item = await this.request("PATCH", `/v1/sites/${encodeURIComponent(slug)}/menu/items/${encodeURIComponent(id)}`, input, true);
        clearMenuCache(this.menuCacheStore, slug);
        return item;
    }
    /**
     * Owner/admin PATCH of a menu category (Commerce / admin).
     * @deprecated Use `client.catalog.patchMenuCategory` (#1044); kept as an alias on /v1.
     */
    async patchMenuCategory(slug, id, input) {
        const cat = await this.request("PATCH", `/v1/sites/${encodeURIComponent(slug)}/menu/categories/${encodeURIComponent(id)}`, input, true);
        clearMenuCache(this.menuCacheStore, slug);
        return cat;
    }
    /**
     * Owner/admin DELETE of a menu item (Commerce / admin).
     * @deprecated Use `client.catalog.deleteMenuItem` (#1044); kept as an alias on /v1.
     */
    async deleteMenuItem(slug, id) {
        await this.request("DELETE", `/v1/sites/${encodeURIComponent(slug)}/menu/items/${encodeURIComponent(id)}`, undefined, true);
        clearMenuCache(this.menuCacheStore, slug);
    }
    /**
     * Owner/admin DELETE of a menu category (Commerce / admin).
     * @deprecated Use `client.catalog.deleteMenuCategory` (#1044); kept as an alias on /v1.
     */
    async deleteMenuCategory(slug, id) {
        await this.request("DELETE", `/v1/sites/${encodeURIComponent(slug)}/menu/categories/${encodeURIComponent(id)}`, undefined, true);
        clearMenuCache(this.menuCacheStore, slug);
    }
    /**
     * Creates a Stripe billing-portal session and returns its URL. Pass the URL
     * to redirect the customer; returnUrl is where Stripe sends them back.
     */
    async createBillingPortalSession(returnUrl) {
        return this.request("POST", "/v1/billing/portal", returnUrl ? { returnUrl } : {}, true);
    }
    /** Returns hosting/subscription state for the signed-in tenant. */
    async getBillingStatus() {
        return this.request("GET", "/v1/billing/status", undefined, true);
    }
    /**
     * Starts (or resumes setup of) a metered subscription: the chosen hosting
     * plan plus the metered traffic price. Returns the client secret the Stripe
     * Payment Element needs to confirm the first payment.
     */
    async subscribe(input) {
        return this.request("POST", "/v1/billing/subscribe", input, true);
    }
    /** Returns the signed-in tenant's usage for the current billing period. */
    async getUsage() {
        return this.request("GET", "/v1/usage", undefined, true);
    }
    /** Tenant data-portability export (GET /v1/export, backup:export capability). */
    async downloadSiteExport(retried401 = false) {
        const headers = {};
        const token = this.getAccessToken();
        if (token)
            headers["Authorization"] = `Bearer ${token}`;
        const res = await this.fetchImpl(`${this.baseUrl}/v1/export`, { method: "GET", headers });
        const text = await res.text();
        if (!res.ok) {
            const data = text ? JSON.parse(text) : {};
            if (res.status === 401 && !retried401) {
                try {
                    await this.ensureRefreshed();
                }
                catch {
                    this.setTokens(null);
                    this.onUnauthorized?.();
                    throw new ApiError(res.status, data.code ?? "error", data.error ?? res.statusText);
                }
                return this.downloadSiteExport(true);
            }
            throw new ApiError(res.status, data.code ?? "error", data.error ?? res.statusText);
        }
        const document = JSON.parse(text);
        const filename = parseContentDispositionFilename(res.headers.get("Content-Disposition"), "site-export-v1.json");
        return { document, filename };
    }
    /** Public kitchen-ticket lookup (no auth). Token is the QR `t` value. */
    async getPublicOrderStatus(slug, token) {
        const q = new URLSearchParams({ t: token });
        return this.request("GET", `/v1/sites/${encodeURIComponent(slug)}/orders/status?${q.toString()}`);
    }
    /** Server price for an on-site checkout (display only; no side effects). #1118 */
    async quoteOnsiteCheckout(slug, input) {
        return this.request("POST", `${this.siteBase(slug)}/checkout/onsite/quote`, input);
    }
    /**
     * Creates the awaiting-payment order and the Stripe Elements session. Keep the
     * idempotency key across retries of the same cart/address; change it when either changes.
     */
    async startOnsiteCheckout(slug, input, idempotencyKey) {
        return this.request("POST", `${this.siteBase(slug)}/checkout/onsite`, input, false, false, {
            "Idempotency-Key": idempotencyKey,
        });
    }
    /** Public, token-gated payment status poll for an on-site order. */
    async getOnsiteOrderStatus(slug, orderId, token) {
        return this.request("GET", `${this.siteBase(slug)}/checkout/onsite/${encodeURIComponent(orderId)}/status?token=${encodeURIComponent(token)}`);
    }
    /** Expires an unpaid on-site session so the customer can start again. */
    async cancelOnsiteCheckout(slug, orderId, token) {
        return this.request("POST", `${this.siteBase(slug)}/checkout/onsite/${encodeURIComponent(orderId)}/cancel?token=${encodeURIComponent(token)}`);
    }
}
//# sourceMappingURL=site.js.map